import React, { useState, ReactElement } from 'react';
import { Shield, ChevronRight } from 'lucide-react';

interface Service {
  id: string;
  title: string;
  icon: ReactElement;  // Changed from JSX.Element to ReactElement
  description: string;
  details: {
    title: string;
    items: string[];
  }[];
}

const Services: React.FC = () => {
  const [activeService, setActiveService] = useState<string | null>(null);

  const services: Service[] = [
    {
      id: 'strategic',
      title: 'Strategic Security Leadership',
      icon: <Shield className="w-12 h-12" />,
      description: 'Comprehensive security strategy and leadership solutions.',
      details: [
        {
          title: 'Virtual CISO (vCISO) Services',
          items: [
            'Security strategy development and execution',
            'Board and executive advisory services',
            'Security roadmap development',
            'Budget planning and optimization',
            'Security team development and mentoring'
          ]
        },
        {
          title: 'Program Management',
          items: [
            'Security program development',
            'Policy and procedure framework',
            'Risk management program',
            'Metrics and reporting framework',
            'Vendor security management'
          ]
        },
        {
          title: 'Compliance Oversight',
          items: [
            'Regulatory compliance management',
            'Audit preparation and management',
            'Standards implementation (ISO 27001, NIST)',
            'Industry-specific compliance (HIPAA, PCI DSS)',
            'Privacy program management'
          ]
        }
      ]
    },
    // ... rest of your services array
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-700 to-blue-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl max-w-3xl">
            Comprehensive cybersecurity solutions designed to protect your organization
            in an ever-evolving threat landscape.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => setActiveService(activeService === service.id ? null : service.id)}
            >
              <div className="p-8">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-purple-700 mb-4">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                  </div>
                  <div className={`transform transition-transform ${activeService === service.id ? 'rotate-180' : ''}`}>
                    <ChevronRight className="w-6 h-6 text-gray-400" />
                  </div>
                </div>

                {/* Expandable Content */}
                <div className={`mt-4 space-y-6 ${activeService === service.id ? 'block' : 'hidden'}`}>
                  {service.details.map((detail, index) => (
                    <div key={index}>
                      <h4 className="text-lg font-semibold mb-3 text-purple-700">{detail.title}</h4>
                      <ul className="space-y-2">
                        {detail.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start">
                            <div className="flex-shrink-0 w-1.5 h-1.5 mt-2 rounded-full bg-blue-500 mr-3"></div>
                            <span className="text-gray-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Contact us today to discuss how our comprehensive security services can help protect your organization.
          </p>
          <button 
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-700 hover:bg-purple-800 transition-colors"
          >
            Schedule a Consultation <ChevronRight className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Services;